/**
 * Durable, same-browser recovery metadata for installer work in progress.
 *
 * Why this exists: every recovery note the installer app kept lived in
 * `sessionStorage`, which is thrown away the moment the tab is closed. An
 * installer who submits an invoice on site, loses signal, and closes the tab
 * came back with no record that an attempt had ever been made — and the only
 * safe thing left was to start a second one. That is exactly the risk the
 * attempt identities were introduced to remove.
 *
 * So the *identity* of an unresolved attempt is now written to `localStorage`,
 * which survives closing the tab and reopening the browser on the same device.
 *
 * Hard rules, enforced by the callers of this module:
 *  - identity and fingerprints only. No file bytes, base64, previews, signed
 *    URLs, auth tokens, private message text, customer notes, or a copy of the
 *    confidential invoice payload.
 *  - the record is UNTRUSTED. It names what to look for on the server; the
 *    server row is what proves anything. Nothing is ever shown as saved on the
 *    strength of a local record.
 *  - it is scoped to one account and one job, and a record belonging to anyone
 *    else is ignored, never repaired or guessed at.
 *
 * Scope that must be stated plainly to the installer: this is the same browser
 * on the same device. It is not another device, not another browser, and it
 * does not mean anything is uploading in the background.
 */

export const SAME_BROWSER_NOTE =
  "This is remembered in this browser on this device only — not on another device or another browser, and nothing uploads in the background.";

export const DURABLE_UNAVAILABLE_NOTE =
  "This device is not letting us keep a local note of unfinished work. Finish and confirm this on screen before you leave the page, or ask the EvoHome Improvements office to check it for you.";

export interface DurableResult<T> {
  ok: boolean;
  value: T;
  reason?: string;
}

function pick(kind: "local" | "session"): Storage | null {
  try {
    const s = kind === "local" ? globalThis.localStorage : globalThis.sessionStorage;
    if (!s) return null;
    return s;
  } catch {
    return null;
  }
}

export function durableStorage(): Storage | null {
  return pick("local");
}

export function sessionStorageOrNull(): Storage | null {
  return pick("session");
}

/**
 * Prove that a record can actually be written and read back BEFORE relying on
 * it. Private browsing, a full quota and a locked-down device all fail here,
 * and the caller must warn the installer before starting an unrecoverable
 * write rather than after it.
 */
export function durablePersistenceWorks(): boolean {
  const s = durableStorage();
  if (!s) return false;
  const probe = "evohome.installer.probe";
  try {
    s.setItem(probe, "1");
    const back = s.getItem(probe);
    s.removeItem(probe);
    return back === "1";
  } catch {
    return false;
  }
}

export function writeDurable(key: string, value: unknown): DurableResult<null> {
  const s = durableStorage();
  if (!s) return { ok: false, value: null, reason: DURABLE_UNAVAILABLE_NOTE };
  try {
    s.setItem(key, JSON.stringify(value));
    return { ok: true, value: null };
  } catch {
    return { ok: false, value: null, reason: DURABLE_UNAVAILABLE_NOTE };
  }
}

export function readDurableRaw(key: string): DurableResult<string | null> {
  const s = durableStorage();
  if (!s) return { ok: false, value: null, reason: DURABLE_UNAVAILABLE_NOTE };
  try {
    return { ok: true, value: s.getItem(key) };
  } catch {
    return { ok: false, value: null, reason: DURABLE_UNAVAILABLE_NOTE };
  }
}

export function removeDurable(key: string): DurableResult<null> {
  const s = durableStorage();
  if (!s) return { ok: false, value: null, reason: DURABLE_UNAVAILABLE_NOTE };
  try {
    s.removeItem(key);
    return { ok: true, value: null };
  } catch {
    return { ok: false, value: null, reason: DURABLE_UNAVAILABLE_NOTE };
  }
}

/**
 * Move a record written by an older build from the tab-only store into the
 * durable one, without creating a second attempt: the durable copy always
 * wins, and the session copy is only promoted when nothing durable exists.
 */
export function migrateSessionRecord(key: string): void {
  const session = sessionStorageOrNull();
  const durable = durableStorage();
  if (!session || !durable) return;
  try {
    const existing = durable.getItem(key);
    if (existing !== null) {
      session.removeItem(key);
      return;
    }
    const legacy = session.getItem(key);
    if (legacy === null) return;
    durable.setItem(key, legacy);
    session.removeItem(key);
  } catch {
    /* a device that refuses storage simply has nothing to migrate */
  }
}

/* ------------------------------------------------------------------ */
/* Fingerprints for content that must never be stored in the clear     */
/* ------------------------------------------------------------------ */

function toHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let out = "";
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, "0");
  return out;
}

/**
 * SHA-256 of a piece of text. Used so an unfinished private message or an
 * invoice's commercial detail can be recognised again after the tab is
 * reopened WITHOUT the words themselves ever being written to the device.
 * A digest cannot be turned back into the text.
 */
export async function digestText(text: string): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle || typeof subtle.digest !== "function") return null;
    const bytes = new TextEncoder().encode(text);
    const buf = await subtle.digest("SHA-256", bytes.slice().buffer as ArrayBuffer);
    return toHex(buf);
  } catch {
    return null;
  }
}
